import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalStore } from '@digiwedge/global-state';
import { ReviewPromptModal } from '../components/Reviews/ReviewPromptModal.js';
import { usePendingReviewPrompt } from '../hooks/usePendingReviewPrompt.js';
/**
 * Provider that displays pending review prompts as a modal overlay.
 * Wrap your app with this provider to enable review prompts on web.
 *
 * @example
 * ```tsx
 * <ReviewPromptProvider>
 *   <Routes>
 *     <Route path="/" element={<HomePage />} />
 *   </Routes>
 * </ReviewPromptProvider>
 * ```
 */
export function ReviewPromptProvider({ children, showDelayMs = 3000, enabled = true, }) {
    const navigate = useNavigate();
    const isAuthenticated = useGlobalStore((s) => s.isAuthenticated);
    const [modalVisible, setModalVisible] = useState(false);
    const [hasShownThisSession, setHasShownThisSession] = useState(false);
    const { currentPrompt, loading, dismissCurrentPrompt, skipToNextPrompt, } = usePendingReviewPrompt({
        enabled: enabled && isAuthenticated,
    });
    // Show modal after delay when there's a prompt and user is authenticated
    useEffect(() => {
        if (!enabled || !isAuthenticated || !currentPrompt || hasShownThisSession || loading) {
            return;
        }
        const timer = setTimeout(() => {
            setModalVisible(true);
            setHasShownThisSession(true);
        }, showDelayMs);
        return () => clearTimeout(timer);
    }, [enabled, isAuthenticated, currentPrompt, hasShownThisSession, loading, showDelayMs]);
    const handleWriteReview = useCallback(() => {
        if (!currentPrompt)
            return;
        setModalVisible(false);
        // Build review URL with query params
        const params = new URLSearchParams({
            bookingId: currentPrompt.bookingId,
            courseId: currentPrompt.courseId,
        });
        if (currentPrompt.courseName)
            params.set('courseName', currentPrompt.courseName);
        if (currentPrompt.clubName)
            params.set('clubName', currentPrompt.clubName);
        if (currentPrompt.teeTime)
            params.set('teeTime', currentPrompt.teeTime);
        navigate(`/review?${params.toString()}`);
    }, [currentPrompt, navigate]);
    const handleDismiss = useCallback(async () => {
        setModalVisible(false);
        await dismissCurrentPrompt();
    }, [dismissCurrentPrompt]);
    const handleRemindLater = useCallback(() => {
        setModalVisible(false);
        skipToNextPrompt();
    }, [skipToNextPrompt]);
    return (_jsxs(_Fragment, { children: [children, _jsx(ReviewPromptModal, { open: modalVisible, prompt: currentPrompt, onWriteReview: handleWriteReview, onDismiss: handleDismiss, onRemindLater: handleRemindLater })] }));
}
export default ReviewPromptProvider;
//# sourceMappingURL=ReviewPromptProvider.js.map